import {
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import Header from "src/components/Header";
import sugerenciaPs from "src/services/sugerenciaPs";

// Sugerencia por Geolocalizacion: el reporte de EMSYS (Reportes > Sugerencia
// por Geolocalizacion) en la app. El calculo es el de EMSYS; aca solo se piden los
// mismos datos y se muestran en tarjetas.
//
//  - Por punto de servicio: se elige un PS y salen los operarios que viven mas
//    cerca (10, 20 o 50), ordenados por tiempo de viaje real.
//  - Por operario: los 5 PS mas cercanos a la casa de cada operario, con los
//    mismos filtros que la web. Primero llega ordenado en linea recta y despues
//    se refina con Google Maps de a tandas, igual que en EMSYS.

type PsCatalogo = { id: number; CodPuntoServicio: string; NombrePServicio: string; Cliente__Cliente: string };

type Catalogo = {
  puntos: PsCatalogo[];
  cantidades: number[];
  top: number;
  limite_maps: number;
  radio_max: number;
};

type PsItem = {
  id: number;
  codigo: string;
  nombre: string;
  cliente: string;
  ciudad?: string;
  vacante_min?: number | null;
  km_linea_recta?: number | null;
  km_ruta?: number | null;
  minutos?: number | null;
  modo?: string | null;
  es_actual?: boolean;
};

type FilaOperario = {
  operario_id: number;
  legajo: string;
  cedula: string;
  nombre: string;
  movilidad: boolean;
  sugerencias: PsItem[];
  actuales: PsItem[];
  ya_en_el_mejor: boolean;
};

type FilaCercano = {
  operario_id: number;
  legajo: string;
  cedula: string;
  nombre: string;
  movilidad: boolean;
  km_linea_recta: number;
  km_ruta?: number | null;
  minutos?: number | null;
  modo?: string | null;
  actuales: PsItem[];
};

type Descartado = { operario_id: number; legajo: string; cedula: string; nombre: string; motivo: string; actuales: PsItem[] };

const MODO: Record<string, string> = { TWO_WHEELER: "moto", TRANSIT: "colectivo", DRIVE: "auto" };
const MOTIVO: Record<string, string> = {
  sin_ubicacion: "Sin coordenadas de domicilio",
  ubicacion_generica: "Coordenada por defecto",
};
// Tope de la tanda; EMSYS manda su limite en el catalogo (limite_maps) y se usa el menor.
const TANDA_MAPS = 25;
const EN_PARALELO = 2;
const POR_PAGINA = 30;

const garnet = "var(--garnet)";
const estilos = {
  tarjeta: { border: "1px solid #e5e5e5", borderRadius: 8, padding: "10px 12px", margin: "10px 0", background: "#fff" },
  nombre: { fontWeight: 600 as const },
  det: { color: "#6c757d", fontSize: "0.82em" },
  etiqueta: { fontSize: "0.72em", textTransform: "uppercase" as const, color: "#6c757d", marginTop: 8 },
  marca: { marginLeft: 6, padding: "1px 6px", borderRadius: 4, fontSize: "0.7em", fontWeight: 600, color: "#fff", background: "#2e7d4f" },
  numero: { display: "inline-block", minWidth: 22, fontWeight: 700, color: garnet },
};

const fmt = (n?: number | null) => (n === null || n === undefined ? "" : String(n).replace(".", ","));
const horas = (min?: number | null) => `${fmt(Math.round(((min || 0) / 60) * 10) / 10)} h`;

const distancia = (it?: { minutos?: number | null; km_ruta?: number | null; km_linea_recta?: number | null; modo?: string | null }) => {
  if (!it) return "";
  if (it.minutos !== null && it.minutos !== undefined) {
    return `${Math.round(it.minutos)} min en ${MODO[it.modo || ""] || it.modo} · ${fmt(it.km_ruta)} km`;
  }
  if (it.km_linea_recta === null || it.km_linea_recta === undefined) return "";
  return `${fmt(it.km_linea_recta)} km en línea recta`;
};

const errorDe = (error: any, defecto: string) => error?.response?.data?.error || defecto;

const ListaActuales: React.FC<{ actuales: PsItem[]; vacio?: string }> = ({ actuales, vacio }) => {
  if (!actuales || actuales.length === 0) {
    return <div style={estilos.det}>{vacio || "Sin asignación vigente"}</div>;
  }
  return (
    <>
      {actuales.map((a) => (
        <div key={a.id} style={{ marginTop: 2 }}>
          <span>{a.nombre}</span>
          <div style={estilos.det}>
            {a.cliente}
            {distancia(a) ? ` · ${distancia(a)}` : ""}
          </div>
        </div>
      ))}
    </>
  );
};

const SugerenciaPs: React.FC = () => {
  const [present] = useIonToast();
  const [segmento, setSegmento] = useState<"ps" | "operario">("ps");
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);

  // Filtros comunes
  const [alcanceOperarios, setAlcanceOperarios] = useState("libres");
  const [radio, setRadio] = useState("");

  // Por punto de servicio
  const [filtroPs, setFiltroPs] = useState("");
  const [psElegido, setPsElegido] = useState<PsCatalogo | null>(null);
  const [cantidad, setCantidad] = useState("10");
  const [cargandoPs, setCargandoPs] = useState(false);
  const [resultadoPs, setResultadoPs] = useState<any>(null);
  // Cada busqueda por PS invalida la anterior: si se cambia de PS mientras la
  // primera sigue calculando, su respuesta no se muestra bajo el PS nuevo.
  const consultaPs = useRef(0);

  // Por operario
  const [alcancePs, setAlcancePs] = useState("vacante");
  const [texto, setTexto] = useState("");
  const [filas, setFilas] = useState<FilaOperario[]>([]);
  const [descartados, setDescartados] = useState<Descartado[]>([]);
  const [resumen, setResumen] = useState<any>(null);
  // Alcance de PS con el que se armo la lista: la etiqueta "vacante" sigue a la
  // consulta, no al combo (que se puede cambiar despues).
  const [conVacante, setConVacante] = useState(true);
  const [cargandoOp, setCargandoOp] = useState(false);
  const [progresoMaps, setProgresoMaps] = useState("");
  const [buscar, setBuscar] = useState("");
  const [visibles, setVisibles] = useState(POR_PAGINA);
  const [verDescartados, setVerDescartados] = useState(false);
  // Cada consulta nueva invalida las tandas de Maps que sigan en vuelo.
  const consulta = useRef(0);

  useEffect(() => {
    const cargar = async () => {
      setCargandoCatalogo(true);
      try {
        const res = await sugerenciaPs.catalogo();
        setCatalogo(res.data);
      } catch (error: any) {
        present({ message: errorDe(error, "No se pudieron cargar los filtros"), duration: 4000, color: "danger" });
      } finally {
        setCargandoCatalogo(false);
      }
    };
    cargar();
    return () => {
      consulta.current += 1;
    };
  }, [present]);

  const puntosFiltrados = useMemo(() => {
    const filtro = filtroPs.trim().toLowerCase();
    if (!filtro || !catalogo) return [];
    return catalogo.puntos
      .filter(
        (p) =>
          (p.NombrePServicio || "").toLowerCase().includes(filtro) ||
          (p.CodPuntoServicio || "").toLowerCase().includes(filtro) ||
          (p.Cliente__Cliente || "").toLowerCase().includes(filtro),
      )
      .slice(0, 40);
  }, [catalogo, filtroPs]);

  const buscarPorPs = async (ps: PsCatalogo | null = psElegido) => {
    if (!ps) {
      present({ message: "Elegí un punto de servicio", duration: 3000, color: "warning" });
      return;
    }
    const idConsulta = ++consultaPs.current;
    setCargandoPs(true);
    setResultadoPs(null);
    try {
      const res = await sugerenciaPs.porPs({
        ps_id: String(ps.id),
        operarios: alcanceOperarios,
        cantidad,
        radio,
        maps: "1",
      });
      if (idConsulta !== consultaPs.current) return;
      setResultadoPs(res.data);
    } catch (error: any) {
      if (idConsulta !== consultaPs.current) return;
      present({ message: errorDe(error, "No se pudo armar la sugerencia"), duration: 4000, color: "danger" });
    } finally {
      if (idConsulta === consultaPs.current) setCargandoPs(false);
    }
  };

  const elegirPs = (ps: PsCatalogo) => {
    setPsElegido(ps);
    setFiltroPs("");
    buscarPorPs(ps);
  };

  const paramsOperario = (extra: Record<string, string>) => ({
    ps: alcancePs,
    operarios: alcanceOperarios,
    q: texto.trim(),
    radio,
    ...extra,
  });

  // Refina con Maps de a tandas de 25: cada tanda reemplaza sus filas apenas llega.
  const refinar = async (idConsulta: number, lista: FilaOperario[]) => {
    const ids = lista.map((f) => f.operario_id);
    const tandas: number[][] = [];
    const tamano = Math.max(1, Math.min(TANDA_MAPS, catalogo?.limite_maps || TANDA_MAPS));
    for (let i = 0; i < ids.length; i += tamano) tandas.push(ids.slice(i, i + tamano));
    let siguiente = 0;
    let hechos = 0;
    let fallidos = 0;
    setProgresoMaps(`Tiempos reales con Maps: 0 de ${ids.length}`);

    const trabajar = async (): Promise<void> => {
      if (idConsulta !== consulta.current || siguiente >= tandas.length) return;
      const tanda = tandas[siguiente++];
      try {
        const res = await sugerenciaPs.porOperario(paramsOperario({ maps: "1", ids: tanda.join(",") }));
        if (idConsulta !== consulta.current) return;
        const nuevas: Record<number, FilaOperario> = {};
        (res.data?.filas || []).forEach((f: FilaOperario) => {
          nuevas[f.operario_id] = f;
        });
        setFilas((prev) => prev.map((f) => nuevas[f.operario_id] || f));
        if (res.data?.resumen?.criterio !== "maps") fallidos += tanda.length;
      } catch {
        fallidos += tanda.length;
      }
      if (idConsulta !== consulta.current) return;
      hechos += tanda.length;
      setProgresoMaps(`Tiempos reales con Maps: ${hechos} de ${ids.length}`);
      return trabajar();
    };

    await Promise.all(Array.from({ length: EN_PARALELO }, () => trabajar()));
    if (idConsulta !== consulta.current) return;
    setProgresoMaps(fallidos ? `${fallidos} operarios quedaron en línea recta: Google Maps no respondió.` : "");
  };

  const buscarPorOperario = async () => {
    const idConsulta = ++consulta.current;
    setCargandoOp(true);
    setProgresoMaps("");
    setVisibles(POR_PAGINA);
    try {
      const res = await sugerenciaPs.porOperario(paramsOperario({ maps: "0" }));
      if (idConsulta !== consulta.current) return;
      const lista: FilaOperario[] = res.data?.filas || [];
      setFilas(lista);
      setConVacante(alcancePs === "vacante");
      setDescartados(res.data?.descartados || []);
      setResumen(res.data?.resumen || null);
      setCargandoOp(false);
      // Siempre con Google Maps: la lista llega en linea recta y se refina de a tandas.
      if (lista.length) await refinar(idConsulta, lista);
    } catch (error: any) {
      if (idConsulta !== consulta.current) return;
      present({ message: errorDe(error, "No se pudo armar la sugerencia"), duration: 4000, color: "danger" });
    } finally {
      if (idConsulta === consulta.current) setCargandoOp(false);
    }
  };

  const filasFiltradas = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return filas;
    return filas.filter((f) =>
      [f.nombre, f.legajo, f.cedula, ...f.actuales.map((a) => a.nombre), ...f.sugerencias.map((s) => `${s.nombre} ${s.cliente}`)]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [filas, buscar]);

  const top = catalogo?.top || 5;

  const filtroRadio = (
    <IonItem>
      <IonLabel position="stacked">Radio máximo (km)</IonLabel>
      <IonInput
        type="number"
        inputmode="numeric"
        min="1"
        max={String(catalogo?.radio_max || 100)}
        value={radio}
        placeholder="Sin límite"
        onIonChange={(e) => setRadio(e.detail.value || "")}
      />
    </IonItem>
  );

  const selectorOperarios = (
    <IonItem>
      <IonLabel position="stacked">Operarios</IonLabel>
      <IonSelect value={alcanceOperarios} interface="action-sheet" onIonChange={(e) => setAlcanceOperarios(e.detail.value)}>
        <IonSelectOption value="libres">Libres (sin asignación vigente)</IonSelectOption>
        <IonSelectOption value="asignados">Asignados (ignorando su asignación actual)</IonSelectOption>
        <IonSelectOption value="todos">Todos</IonSelectOption>
      </IonSelect>
    </IonItem>
  );

  return (
    <IonPage>
      <Header titulo="Sugerencia por Geolocalización" />
      <IonContent id="sugerencia-ps" className="ion-padding">
        <div style={{ ...estilos.det, marginBottom: 8 }}>
          Único criterio: la distancia entre la casa del operario y el punto de servicio. Solo puntos de servicio de Mantenimiento.
        </div>
        <IonSegment value={segmento} onIonChange={(e) => setSegmento(e.detail.value as "ps" | "operario")}>
          <IonSegmentButton value="ps">
            <IonLabel>Por punto de servicio</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="operario">
            <IonLabel>Por operario</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {cargandoCatalogo && (
          <IonItem lines="none">
            <IonSpinner slot="start" />
            <IonLabel>Cargando filtros...</IonLabel>
          </IonItem>
        )}

        {segmento === "ps" && catalogo && (
          <>
            {!psElegido ? (
              <>
                <IonItem>
                  <IonLabel position="stacked">Punto de servicio</IonLabel>
                  <IonInput
                    value={filtroPs}
                    placeholder="Nombre, código o cliente"
                    onIonChange={(e) => setFiltroPs(e.detail.value || "")}
                  />
                </IonItem>
                {filtroPs.trim().length > 0 && (
                  <IonList>
                    {puntosFiltrados.map((ps) => (
                      <IonItem key={ps.id} button onClick={() => elegirPs(ps)}>
                        <IonLabel>
                          <strong>{ps.NombrePServicio}</strong>
                          <div style={estilos.det}>
                            {ps.CodPuntoServicio} · {ps.Cliente__Cliente}
                          </div>
                        </IonLabel>
                      </IonItem>
                    ))}
                    {puntosFiltrados.length === 0 && (
                      <IonItem lines="none">
                        <IonLabel>No hay puntos de servicio con ese filtro.</IonLabel>
                      </IonItem>
                    )}
                  </IonList>
                )}
              </>
            ) : (
              <IonItem lines="full">
                <IonLabel>
                  <small style={{ color: "var(--grey)" }}>Punto de servicio</small>
                  <div>
                    <strong>{psElegido.NombrePServicio}</strong>
                  </div>
                  <div style={estilos.det}>
                    {psElegido.CodPuntoServicio} · {psElegido.Cliente__Cliente}
                  </div>
                </IonLabel>
                <IonButton slot="end" fill="outline" size="small" onClick={() => { consultaPs.current += 1; setCargandoPs(false); setPsElegido(null); setResultadoPs(null); }}>
                  Cambiar
                </IonButton>
              </IonItem>
            )}

            {selectorOperarios}
            <IonItem>
              <IonLabel position="stacked">Cantidad</IonLabel>
              <IonSelect value={cantidad} interface="action-sheet" onIonChange={(e) => setCantidad(e.detail.value)}>
                {catalogo.cantidades.map((n) => (
                  <IonSelectOption key={n} value={String(n)}>
                    Los {n} más cercanos
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            {filtroRadio}
            <IonButton expand="block" className="ion-margin-top" onClick={() => buscarPorPs()} disabled={cargandoPs || !psElegido}>
              {cargandoPs ? <IonSpinner name="crescent" /> : "Buscar operarios cercanos"}
            </IonButton>

            {resultadoPs && (
              <>
                <div style={{ ...estilos.det, marginTop: 12 }}>
                  {resultadoPs.ps?.ciudad && resultadoPs.ps.ciudad !== "N/A" ? `${resultadoPs.ps.ciudad} · ` : ""}
                  vacante {horas(resultadoPs.ps?.vacante_min)} · {resultadoPs.resumen?.en_radio} operarios en el alcance
                  {resultadoPs.resumen?.ya_en_el_ps ? ` · ${resultadoPs.resumen.ya_en_el_ps} ya trabajan acá (no se listan)` : ""}
                  {resultadoPs.resumen?.criterio === "linea_recta" ? " · Google Maps no respondió: orden en línea recta" : ""}
                </div>
                {(resultadoPs.filas || []).map((f: FilaCercano, i: number) => (
                  <div key={f.operario_id} style={estilos.tarjeta}>
                    <div>
                      <span style={estilos.numero}>{i + 1}</span>
                      <span style={estilos.nombre}>{f.nombre}</span>
                    </div>
                    <div style={estilos.det}>
                      Leg. {f.legajo} · CI {f.cedula}
                      {f.movilidad ? " · con movilidad" : ""}
                    </div>
                    <div style={{ marginTop: 4, fontWeight: 600 }}>{distancia(f)}</div>
                    <div style={estilos.etiqueta}>Trabaja hoy en</div>
                    <ListaActuales actuales={f.actuales} vacio="Libre" />
                  </div>
                ))}
                {(resultadoPs.filas || []).length === 0 && (
                  <IonItem lines="none">
                    <IonLabel>Ningún operario dentro del alcance.</IonLabel>
                  </IonItem>
                )}
              </>
            )}
          </>
        )}

        {segmento === "operario" && catalogo && (
          <>
            <IonItem>
              <IonLabel position="stacked">Operario (opcional)</IonLabel>
              <IonInput
                value={texto}
                placeholder="Legajo, cédula o nombre"
                onIonChange={(e) => setTexto(e.detail.value || "")}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Puntos de servicio</IonLabel>
              <IonSelect value={alcancePs} interface="action-sheet" onIonChange={(e) => setAlcancePs(e.detail.value)}>
                <IonSelectOption value="vacante">Solo con horas vacantes</IonSelectOption>
                <IonSelectOption value="activos">Todos los activos</IonSelectOption>
              </IonSelect>
            </IonItem>
            {selectorOperarios}
            {filtroRadio}
            <IonButton expand="block" className="ion-margin-top" onClick={buscarPorOperario} disabled={cargandoOp}>
              {cargandoOp ? <IonSpinner name="crescent" /> : "Consultar"}
            </IonButton>
            <div style={{ ...estilos.det, marginTop: 6 }}>Sin filtros se listan todos los operarios.</div>

            {resumen && (
              <>
                <div style={{ ...estilos.det, marginTop: 12 }}>
                  {filas.filter((f) => f.sugerencias.length).length} operarios con sugerencia · {resumen.ps_considerados} PS considerados
                  {filas.filter((f) => f.ya_en_el_mejor).length
                    ? ` · ${filas.filter((f) => f.ya_en_el_mejor).length} ya en el más cercano`
                    : ""}
                </div>
                {progresoMaps && (
                  <div style={{ ...estilos.det, marginTop: 4 }}>
                    <IonSpinner name="dots" style={{ height: 14, verticalAlign: "middle" }} /> {progresoMaps}
                  </div>
                )}
                {filas.length > 5 && (
                  <IonItem>
                    <IonInput
                      value={buscar}
                      placeholder="Buscar en los resultados"
                      onIonChange={(e) => {
                        setBuscar(e.detail.value || "");
                        setVisibles(POR_PAGINA);
                      }}
                    />
                  </IonItem>
                )}

                {filasFiltradas.slice(0, visibles).map((f) => (
                  <div key={f.operario_id} style={estilos.tarjeta}>
                    <div style={estilos.nombre}>{f.nombre}</div>
                    <div style={estilos.det}>
                      Leg. {f.legajo} · CI {f.cedula}
                      {f.movilidad ? " · con movilidad" : ""}
                    </div>
                    <div style={estilos.etiqueta}>Trabaja hoy en</div>
                    <ListaActuales actuales={f.actuales} />
                    <div style={estilos.etiqueta}>Los {top} PS más cercanos</div>
                    {f.sugerencias.map((s, i) => (
                      <div key={s.id} style={{ marginTop: 4 }}>
                        <span style={estilos.numero}>{i + 1}</span>
                        <span>{s.nombre}</span>
                        {s.es_actual && <span style={estilos.marca}>actual</span>}
                        <div style={{ ...estilos.det, marginLeft: 22 }}>
                          {s.cliente}
                          {s.ciudad && s.ciudad !== "N/A" ? ` · ${s.ciudad}` : ""} · {distancia(s)}
                          {conVacante && !s.es_actual ? ` · vacante ${horas(s.vacante_min)}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                {filasFiltradas.length > visibles && (
                  <IonButton expand="block" fill="outline" onClick={() => setVisibles((v) => v + POR_PAGINA)}>
                    Ver más ({filasFiltradas.length - visibles} restantes)
                  </IonButton>
                )}
                {filasFiltradas.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel>Sin resultados.</IonLabel>
                  </IonItem>
                )}

                {descartados.length > 0 && (
                  <>
                    <IonButton fill="clear" size="small" onClick={() => setVerDescartados((v) => !v)}>
                      {verDescartados ? "Ocultar" : "Ver"} operarios sin ubicación confiable ({descartados.length})
                    </IonButton>
                    {verDescartados &&
                      descartados.map((d) => (
                        <div key={d.operario_id} style={estilos.tarjeta}>
                          <div style={estilos.nombre}>{d.nombre}</div>
                          <div style={estilos.det}>
                            Leg. {d.legajo} · CI {d.cedula} · {MOTIVO[d.motivo] || d.motivo}
                          </div>
                          <ListaActuales actuales={d.actuales} />
                        </div>
                      ))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SugerenciaPs;
